import { RecyclingOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Button, Dialog, DialogActions, DialogTitle, Tooltip } from "@mui/material";
import { useResource, useTranslate, useUpdate, useWarnAboutChange } from "@refinedev/core";
import type { DeleteButtonProps } from "@refinedev/mui";
import React from "react";

export const RecycleButton: React.FC<DeleteButtonProps> = ({
  resource: resourceNameFromProps,
  recordItemId,
  onSuccess,
  mutationMode: mutationModeProp,
  children,
  successNotification,
  errorNotification,
  hideText = false,
  accessControl,
  meta,
  dataProviderName,
  confirmTitle,
  confirmOkText,
  confirmCancelText,
  svgIconProps,
  invalidates,
  ...rest
}) => {
  const accessControlEnabled = accessControl?.enabled ?? true;
  const hideIfUnauthorized = accessControl?.hideIfUnauthorized ?? false;
  const translate = useTranslate();

  const { id, resource } = useResource(
    resourceNameFromProps
  );

  const { mutate, isLoading, variables } = useUpdate();

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseOnConfirm = () => {
    if (recordItemId && resource?.name) {
      setWarnWhen(false);
      setOpen(false);
      mutate(
        {
            resource: resource?.name,
            values: {
                status: "0",
            },
            id: recordItemId,
        },
        {
            onSuccess: (value) => {
            onSuccess && onSuccess(value);
            },
        }
      );
    }
  };

  const { sx, ...restProps } = rest;

  const { setWarnWhen } = useWarnAboutChange();

  if (accessControlEnabled && hideIfUnauthorized) {
    return null;
  }

  return (
    <div>
      <Tooltip title='Enable'>
        <LoadingButton
          color="error"
          onClick={handleClickOpen}
          loading={(recordItemId ?? id) === variables?.id && isLoading}
          startIcon={!hideText && <RecyclingOutlined {...svgIconProps} />}
          sx={{ minWidth: 0, ...sx }}
          loadingPosition={hideText ? "center" : "start"}
          {...restProps}
        >
          {hideText ? (
            <RecyclingOutlined fontSize="small" {...svgIconProps} color="success"/>
          ) : (
            children ?? translate("buttons.delete", "Delete")
          )}
        </LoadingButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {confirmTitle ?? translate("buttons.confirm", "Are you sure?")}
        </DialogTitle>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button onClick={handleClose}>
            {confirmCancelText ?? translate("buttons.cancel", "Cancel")}
          </Button>
          <Button color="error" onClick={handleCloseOnConfirm} autoFocus>
            {confirmOkText ?? translate("buttons.delete", "Recycle")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
